import { FinancialAccount } from "@common/types/financialAccount";
// oxlint-disable-next-line no-restricted-imports -- Legacy table migration/demo — do not copy into new code.
import { GridTableHeader } from "@design-system/Tables/GridTable/GridTable";
import classNames from "classnames";
import { AccountCard } from "@components/financialAccount/AccountCard/AccountCard";
import { TriangleIcon } from "@icons";
import { KickAvatar } from "@components/avatar/KickAvatar";
import { format, parseISO } from "date-fns";
import { fullDateLongFormat } from "@common/helpers/date";
import { useMemo, useState } from "react";
import { Button } from "@design-system/Button/Button";
import styles from "./Reconciliation.module.scss";
import { ReconciliationTableHeaderRow } from "./ReconciliationTableHeaderRow";
import { ReconciliationOpeningBalance } from "./ReconciliationOpeningBalance";

export interface ReconciliationTableHeaderProps {
    financialAccount: FinancialAccount;
    statementsOpeningBalance: number;
    statementsClosingBalance: number;
    kickOpeningBalance: number | null;
    kickOpeningBalanceAsOfReconciliationStart: number;
    kickReconciledClosingBalance: number;
    kickUnreconciledClosingBalance: number;
    kickOpeningDate: string;
    openingDate: string;
    closingDate: string;
    statementsCashIn: number;
    statementsCashOut: number;
    kickReconciledCashIn: number;
    kickReconciledCashOut: number;
    kickUnreconciled: number;
    isKickOpeningBalanceEditable?: boolean;
    onOpeningBalanceUpdated: (
        openingBalance: number | null,
    ) => Promise<void> | void;
}

export const ReconciliationTableHeader: React.FC<
    ReconciliationTableHeaderProps
> = ({
    financialAccount,
    statementsOpeningBalance,
    statementsClosingBalance,
    kickOpeningBalance,
    kickOpeningBalanceAsOfReconciliationStart,
    kickReconciledClosingBalance,
    statementsCashIn,
    statementsCashOut,
    kickReconciledCashIn,
    kickReconciledCashOut,
    kickUnreconciled,
    kickOpeningDate,
    openingDate,
    closingDate,
    kickUnreconciledClosingBalance,
    isKickOpeningBalanceEditable,
    onOpeningBalanceUpdated,
}) => {
    const openingDateLabel = useMemo(
        () => format(parseISO(openingDate), fullDateLongFormat),
        [openingDate],
    );
    const closingDateLabel = useMemo(
        () => format(parseISO(closingDate), fullDateLongFormat),
        [closingDate],
    );
    const kickOpeningDateLabel = useMemo(
        () => format(parseISO(kickOpeningDate), fullDateLongFormat),
        [kickOpeningDate],
    );
    const [isExpended, setIsExpended] = useState(true);
    const showKickOpeningDateRow = kickOpeningDate !== openingDate;

    return (
        <>
            <GridTableHeader
                columnBorders
                className={classNames(styles.header, {
                    [styles.expandedHeader]: isExpended,
                })}
            >
                <div className={styles.summaryHeaderCell}>
                    <Button
                        variant="secondary"
                        iconOnly
                        size="sm"
                        onClick={() => setIsExpended(!isExpended)}
                        aria-label={
                            isExpended ? "Collapse summary" : "Expand summary"
                        }
                    >
                        <TriangleIcon
                            className={classNames(
                                "icon-color-gray-500",
                                styles.expandIcon,
                            )}
                        />
                    </Button>
                    <span className={styles.summaryTitle}>Summary</span>
                    <AccountCard
                        account={financialAccount}
                        numberInName
                        showType={false}
                        iconSize="xs"
                    />
                </div>
                <div className={styles.summaryHeaderCell}>
                    <KickAvatar size="xs" aria-hidden />
                    Kick
                </div>
                <div
                    className={classNames(
                        styles.summaryHeaderCell,
                        styles.summaryDifferenceCell,
                    )}
                >
                    Difference
                </div>

                {showKickOpeningDateRow && (
                    <ReconciliationTableHeaderRow
                        kickTitle={`Opening balance on ${kickOpeningDateLabel}`}
                        kickValue={
                            <ReconciliationOpeningBalance
                                openingBalance={kickOpeningBalance}
                                financialAccount={financialAccount}
                                isEditable={isKickOpeningBalanceEditable}
                                onOpeningBalanceUpdated={
                                    onOpeningBalanceUpdated
                                }
                            />
                        }
                        kickValueForCalculations={kickOpeningBalance ?? 0}
                        firstStat
                    />
                )}
                <ReconciliationTableHeaderRow
                    statementsTitle={`Beginning balance on ${openingDateLabel}`}
                    kickTitle={
                        showKickOpeningDateRow
                            ? `Beginning period balance on ${openingDateLabel}`
                            : "Beginning reconciled balance"
                    }
                    statementsValue={statementsOpeningBalance}
                    kickValue={
                        showKickOpeningDateRow ? (
                            kickOpeningBalanceAsOfReconciliationStart
                        ) : (
                            <ReconciliationOpeningBalance
                                openingBalance={kickOpeningBalance}
                                financialAccount={financialAccount}
                                suggestedOpeningBalance={
                                    statementsOpeningBalance
                                }
                                isEditable={isKickOpeningBalanceEditable}
                                onOpeningBalanceUpdated={
                                    onOpeningBalanceUpdated
                                }
                            />
                        )
                    }
                    kickValueForCalculations={
                        showKickOpeningDateRow
                            ? kickOpeningBalanceAsOfReconciliationStart
                            : (kickOpeningBalance ?? 0)
                    }
                    firstStat={!showKickOpeningDateRow}
                />

                <ReconciliationTableHeaderRow
                    statementsTitle="Cash in"
                    kickTitle="Cash in"
                    statementsValue={statementsCashIn}
                    kickValue={kickReconciledCashIn}
                />

                <ReconciliationTableHeaderRow
                    statementsTitle="Cash out"
                    kickTitle="Cash out"
                    statementsValue={statementsCashOut}
                    kickValue={kickReconciledCashOut}
                />

                <ReconciliationTableHeaderRow
                    statementsTitle={`Ending balance on ${closingDateLabel}`}
                    kickTitle="Ending reconciled balance"
                    statementsValue={statementsClosingBalance}
                    kickValue={kickReconciledClosingBalance}
                    alwaysExpanded
                />

                <ReconciliationTableHeaderRow
                    kickTitle="Unreconciled transactions"
                    kickValue={kickUnreconciled}
                />

                <ReconciliationTableHeaderRow
                    kickTitle={`Ending period balance on ${closingDateLabel}`}
                    kickValue={kickUnreconciledClosingBalance}
                    lastStat
                />

                <div className={styles.tableHeaderCell}>Date</div>
                <div className={styles.tableHeaderCell}>Description</div>
                <div
                    className={classNames(
                        styles.tableHeaderCell,
                        styles.headerCellAmount,
                    )}
                >
                    Amount
                </div>

                <div className={styles.tableHeaderCell}>Date</div>
                <div className={styles.tableHeaderCell}>Description</div>
                <div
                    className={classNames(
                        styles.tableHeaderCell,
                        styles.headerCellAmount,
                    )}
                >
                    Amount
                </div>

                <div
                    className={classNames(
                        styles.tableHeaderCell,
                        styles.actionsCell,
                    )}
                />
            </GridTableHeader>
        </>
    );
};
